<template>
	<v-container>
		<v-row>
			<!-- Timezone Selection -->
			<div class="d-flex align-center justify-center" style="width: 100%;">
				<span class="font-weight-bold primary--text" style="font-size: 20px;">
					{{ $t('timeCollection.setTitle') }}
				</span>
			</div>

			<div class="d-flex flex-column my-4" style="width: 100%;">
				<div class="d-flex flex-row mb-2">
					<v-icon class="mr-2" color="black">
						mdi-clock-outline
					</v-icon>
					<span>
						{{ $t('timeCollection.timezoneQuestion') }}
					</span>
				</div>
				<v-select v-model="settings.timezone" :items="TIMEZONES" outlined dense hide-details="auto">
				</v-select>
			</div>

			<!-- Availability Templates -->
			<div class="d-flex flex-column mb-4">
				<div class="d-flex justify-space-between align-center mb-2">
					<span class="subtitle-1 font-weight-medium">{{ $t('timeCollection.quickTemplatesTitle') }}</span>
					<v-btn text outlined class="text-none"
						style="border-radius: 8px;"
						@click="setShowAllHours(!showAllHours)">
						{{ showAllHours ? $t('timeCollection.showBusinessHoursButton') : $t('timeCollection.showAllHoursButton') }}
					</v-btn>
				</div>

				<v-row dense>
					<v-col v-for="template in AVAILABILITY_TEMPLATES_I18N" :key="template.id" cols="6" class="py-1">
						<v-btn text outlined class="justify-start text-none" 
							style="width: 100%; border-radius: 8px;"
							@click="applyTemplate(template.id)">
							{{ template.label }}
						</v-btn>
					</v-col>
				</v-row>
			</div>

			<!-- Calendar-like Weekly Schedule -->
			<div class="d-flex flex-column mb-4" style="width: 100%;">
				<div class="d-flex justify-space-between align-center mb-2">
					<span class="subtitle-1 font-weight-medium">{{ $t('timeCollection.setWeeklyAvailabilityTitle') }}</span>
					<v-tooltip bottom>
						<template v-slot:activator="{ on }">
							<v-icon small color="grey lighten-1" v-on="on">
								mdi-information-outline
							</v-icon>
						</template>
						<span>
							{{ $t('timeCollection.dragInstructionTooltip') }}
						</span>
					</v-tooltip>
				</div>

				<v-sheet class="calendar-container" outlined style="border-radius: 8px;">
					<!-- Header row with days -->
					<div class="d-flex day-header" style="width: 100%;">
						<div class="time-column"></div>
						<div v-for="day in DAYS_OF_WEEK_I18N" :key="day.id"
							class="day-column d-flex align-center justify-center py-2 cursor-pointer"
							:class="settings.availableDays[day.id] ? 'selected-day' : 'unselected-day'" @click="toggleDay(day.id)">
							<div class="d-flex align-center">
								{{ day.label }}
							<v-icon x-small class="ml-1" :color="settings.availableDays[day.id] ? 'primary' : ''">
								{{ settings.availableDays[day.id] ? 'mdi-check' : 'mdi-close' }}
							</v-icon>
							</div>
						</div>
					</div>

					<!-- Time slots grid -->
					<div class="time-grid-container d-flex flex-column" style="width: 100%;">
						<div v-for="hour in getVisibleHours()" :key="hour" class="d-flex time-row">
							<div class="time-column d-flex align-center justify-center caption py-2" style="background-color: rgb(241, 245, 249);">
								{{ formatTime(hour) }}
							</div>

							<div v-for="day in DAYS_OF_WEEK_I18N" :key="`${day.id}-${hour}`"
								class="day-column py-2 d-flex align-center justify-center"
								:class="{
									'cursor-not-allowed': !settings.availableDays[day.id],
									'cursor-pointer': settings.availableDays[day.id],
									'selected-hour': isHourAvailable(day.id, hour) && settings.availableDays[day.id]
								}"
								@mousedown="handleMouseDown(day.id, hour)"
								@mouseenter="handleMouseEnter(day.id, hour)">
								<div v-if="settings.availableDays[day.id]" class="time-indicator"
									:class="{ 'selected': isHourAvailable(day.id, hour) }">
									<v-icon v-if="isHourAvailable(day.id, hour)" x-small color="white">mdi-check</v-icon>
								</div>
							</div>
						</div>
					</div>
				</v-sheet>

				<div class="d-flex align-center mt-2 caption grey--text">
					<v-icon x-small class="mr-1 grey--text">mdi-information-outline</v-icon>
					<span>{{ $t('timeCollection.dragInstructionFooter') }}</span>
				</div>
			</div>

			<!-- Meeting Duration -->
			<div class="d-flex flex-column mb-4" style="width: 100%;">
				<span class="mb-2">
					{{ $t('timeCollection.meetingDurationQuestion') }}
				</span>
				<v-select v-model="settings.meetingDuration" :items="MEETING_DURATIONS_I18N" item-text="label" item-value="value" outlined dense hide-details="auto">
				</v-select>
			</div>

			<!-- Repeat Period -->
			<div class="d-flex flex-column mb-4" style="width: 100%;">
				<span class="mb-2">
					{{ $t('timeCollection.repeatWeeksQuestion') }}
				</span>
				<v-select v-model="settings.repeatWeeks" :items="REPEAT_WEEKS_I18N" item-text="label" item-value="value" outlined dense hide-details="auto">
				</v-select>

				<div class="caption grey--text mt-1">
					{{ $t('timeCollection.repeatUntilDate', { date: new Date(Date.now() + settings.repeatWeeks * 7 * 24 * 60 * 60 * 1000).toLocaleDateString() }) }}
				</div>
			</div>

			<!-- Save Button -->
			<div class="d-flex flex-row justify-end" style="width: 100%;">
				<v-btn color="primary text-none" @click="handleSave()">
					{{ $t('timeCollection.saveButton') }}
				</v-btn>
			</div>
		</v-row>
	</v-container>
</template>

<script>
import Vue from 'vue';

export default Vue.extend({
	name: 'TimeCollection',

	props: {
		initialSettings: {
			type: Object,
			default: null
		}
	},

	data() {
		return {
			TIMEZONES: [
				// 主要常用时区
				"UTC",
				"Asia/Taipei",         // 台北
				"Asia/Shanghai",       // 上海
				"Asia/Tokyo",          // 东京
				"Asia/Singapore",      // 新加坡
				"Europe/London",       // 伦敦
				"Europe/Paris",        // 巴黎
				"Europe/Berlin",       // 柏林
				"America/New_York",    // 纽约（东部时间）
				"America/Chicago",     // 芝加哥（中部时间）
				"America/Denver",      // 丹佛（山区时间）
				"America/Los_Angeles", // 洛杉矶（太平洋时间）
				"Australia/Sydney",    // 悉尼

				// 次常用时区
				"Europe/Madrid",       // 马德里
				"Europe/Moscow",       // 莫斯科
				"Europe/Rome",         // 罗马
				"Asia/Dubai",          // 迪拜
				"Asia/Kolkata",        // 加尔各答（印度标准时间）
				"America/Toronto",     // 多伦多
				"America/Vancouver",   // 温哥华
				"America/Sao_Paulo",   // 圣保罗
				"America/Mexico_City", // 墨西哥城
				"Australia/Perth"      // 珀斯
			],

			MEETING_DURATIONS: [
				// { value: 15, label: "15 minutes" },
				// { value: 30, label: "30 minutes" },
				// { value: 45, label: "45 minutes" },
				{ value: 60, label: "timeCollection.meetingDurations.minutes" }
			],

			// REPEAT_WEEKS: [
			// 	{ value: 2, label: "2 weeks" },
			// 	{ value: 4, label: "1 month (4 weeks)" },
			// 	{ value: 8, label: "2 months (8 weeks)" },
			// 	{ value: 12, label: "3 months (12 weeks)" },
			// ],

			REPEAT_WEEKS: [
				{ value: 2, label: "timeCollection.repeatWeeks.weeks" },
				{ value: 4, label: "timeCollection.repeatWeeks.month" },
				{ value: 8, label: "timeCollection.repeatWeeks.months" },
				{ value: 12, label: "timeCollection.repeatWeeks.months" },
			],

			// DAYS_OF_WEEK: [
			// 	{ id: "monday", label: "Mon" },
			// 	{ id: "tuesday", label: "Tue" },
			// 	{ id: "wednesday", label: "Wed" },
			// 	{ id: "thursday", label: "Thu" },
			// 	{ id: "friday", label: "Fri" },
			// 	{ id: "saturday", label: "Sat" },
			// 	{ id: "sunday", label: "Sun" },
			// ],

			DAYS_OF_WEEK: [
				{ id: "monday", label: "timeCollection.days.monday" },
				{ id: "tuesday", label: "timeCollection.days.tuesday" },
				{ id: "wednesday", label: "timeCollection.days.wednesday" },
				{ id: "thursday", label: "timeCollection.days.thursday" },
				{ id: "friday", label: "timeCollection.days.friday" },
				{ id: "saturday", label: "timeCollection.days.saturday" },
				{ id: "sunday", label: "timeCollection.days.sunday" },
			],

			// AVAILABILITY_TEMPLATES: [
			// 	{ id: "business", label: "Business Hours (9AM-5PM, Mon-Fri)" },
			// 	{ id: "extended", label: "Extended Hours (8AM-8PM, Mon-Fri)" },
			// 	{ id: "weekends", label: "Weekends Only (10AM-4PM, Sat-Sun)" },
			// 	{ id: "mornings", label: "Mornings Only (8AM-12PM, Mon-Fri)" },
			// 	{ id: "afternoons", label: "Afternoons Only (1PM-5PM, Mon-Fri)" },
			// 	{ id: "evenings", label: "Evenings Only (6PM-9PM, Mon-Fri)" },
			// ],

			AVAILABILITY_TEMPLATES: [
				{ id: "business", label: "timeCollection.templates.business" },
				{ id: "extended", label: "timeCollection.templates.extended" },
				{ id: "weekends", label: "timeCollection.templates.weekends" },
				{ id: "mornings", label: "timeCollection.templates.mornings" },
				{ id: "afternoons", label: "timeCollection.templates.afternoons" },
				{ id: "evenings", label: "timeCollection.templates.evenings" },
			],

			// Hours to display in the calendar
			HOURS: Array.from({ length: 16 }, (_, i) => i + 6), // 6 AM to 9 PM

			settings: this.getInitialSettings(),
			isDragging: false,
			dragStartCell: null,
			dragOperation: null,
			showAllHours: false
		};
	},

	computed: {
		DAYS_OF_WEEK_I18N() {
			return this.DAYS_OF_WEEK.map(day => ({
				...day,
				label: this.$t(day.label)
			}));
		},
		AVAILABILITY_TEMPLATES_I18N() {
			return this.AVAILABILITY_TEMPLATES.map(template => ({
				...template,
				label: this.$t(template.label)
			}));
		},
		MEETING_DURATIONS_I18N() {
			return this.MEETING_DURATIONS.map(duration => ({
				...duration,
				label: this.$t(duration.label, { count: duration.value })
			}));
		},
		REPEAT_WEEKS_I18N() {
			return this.REPEAT_WEEKS.map(week => ({
				...week,
				label: this.$t(week.label, { count: week.value, monthCount: week.value/4 })
			}));
		}
	},

	mounted() {
		// Add global mouse up handler to end dragging even if mouse is released outside the grid
		window.addEventListener('mouseup', this.handleMouseUp);
	},

	beforeDestroy() {
		window.removeEventListener('mouseup', this.handleMouseUp);
	},

	methods: {
		getInitialSettings() {
			if (this.initialSettings) return this.initialSettings;

			// Default settings
			return {
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				availableDays: {
					monday: true,
					tuesday: true,
					wednesday: true,
					thursday: true,
					friday: true,
					saturday: false,
					sunday: false,
				},
				availableHours: {
					monday: this.generateDefaultHours(9, 17),
					tuesday: this.generateDefaultHours(9, 17),
					wednesday: this.generateDefaultHours(9, 17),
					thursday: this.generateDefaultHours(9, 17),
					friday: this.generateDefaultHours(9, 17),
					saturday: {},
					sunday: {},
				},
				meetingDuration: 60,
				repeatWeeks: 4,
				lastUpdated: Date.now(),
			};
		},

		// Generate default hours (e.g., 9AM-5PM)
		generateDefaultHours(startHour, endHour) {
			const hours = {};
			for (let hour = 0; hour < 24; hour++) {
				hours[hour] = hour >= startHour && hour < endHour;
			}
			return hours;
		},

		// Apply a template to quickly set availability
		applyTemplate(templateId) {
			const newSettings = { ...this.settings };

			switch (templateId) {
				case "business":
				// Business Hours (9AM-5PM, Mon-Fri)
				this.DAYS_OF_WEEK.forEach((day) => {
					const isWeekday = ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(day.id);
					newSettings.availableDays[day.id] = isWeekday;
					newSettings.availableHours[day.id] = this.generateDefaultHours(9, 17);
				});
				break;

				case "extended":
				// Extended Hours (8AM-8PM, Mon-Fri)
				this.DAYS_OF_WEEK.forEach((day) => {
					const isWeekday = ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(day.id);
					newSettings.availableDays[day.id] = isWeekday;
					newSettings.availableHours[day.id] = this.generateDefaultHours(8, 20);
				});
				break;

				case "weekends":
				// Weekends Only (10AM-4PM, Sat-Sun)
				this.DAYS_OF_WEEK.forEach((day) => {
					const isWeekend = ["saturday", "sunday"].includes(day.id);
					newSettings.availableDays[day.id] = isWeekend;
					newSettings.availableHours[day.id] = this.generateDefaultHours(10, 16);
				});
				break;

				case "mornings":
				// Mornings Only (8AM-12PM, Mon-Fri)
				this.DAYS_OF_WEEK.forEach((day) => {
					const isWeekday = ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(day.id);
					newSettings.availableDays[day.id] = isWeekday;
					newSettings.availableHours[day.id] = this.generateDefaultHours(8, 12);
				});
				break;

				case "afternoons":
				// Afternoons Only (1PM-5PM, Mon-Fri)
				this.DAYS_OF_WEEK.forEach((day) => {
					const isWeekday = ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(day.id);
					newSettings.availableDays[day.id] = isWeekday;
					newSettings.availableHours[day.id] = this.generateDefaultHours(13, 17);
				});
				break;

				case "evenings":
				// Evenings Only (6PM-9PM, Mon-Fri)
				this.DAYS_OF_WEEK.forEach((day) => {
					const isWeekday = ["monday", "tuesday", "wednesday", "thursday", "friday"].includes(day.id);
					newSettings.availableDays[day.id] = isWeekday;
					newSettings.availableHours[day.id] = this.generateDefaultHours(18, 21);
				});
				break;
			}

			this.settings = newSettings;
		},

		// Toggle a day's availability
		toggleDay(dayId) {
			const isCurrentlyEnabled = this.settings.availableDays[dayId];

			// If we're enabling the day, initialize hours if they don't exist
			if (!isCurrentlyEnabled && !this.settings.availableHours[dayId]) {
				this.$set(this.settings.availableHours, dayId, this.generateDefaultHours(9, 17));
			}

			this.$set(this.settings.availableDays, dayId, !isCurrentlyEnabled);
		},

		// Toggle an hour's availability for a specific day
		toggleHour(dayId, hour) {
			const currentHours = this.settings.availableHours[dayId] || {};
			if (!currentHours[hour]) {
				if (!this.settings.availableHours[dayId]) {
					this.$set(this.settings.availableHours, dayId, {});
				}
				this.$set(this.settings.availableHours[dayId], hour, true);
			} else {
				this.$set(this.settings.availableHours[dayId], hour, false);
			}
			
			// 檢查該天是否還有任何可用時間
			const hasAvailableHour = Object.values(this.settings.availableHours[dayId] || {}).some(value => value === true);
			
			// 如果沒有任何可用時間，自動將該天設為不可用
			if (!hasAvailableHour) {
				this.settings.availableDays[dayId] = false;
			}
		},

		// Handle mouse down on a cell
		handleMouseDown(dayId, hour) {
			if (!this.settings.availableDays[dayId]) return;

			this.isDragging = true;
			this.dragStartCell = { day: dayId, hour };

			// Determine if we're selecting or deselecting based on the first cell
			const currentHours = this.settings.availableHours[dayId] || {};
			const isCurrentlySelected = currentHours[hour];
			this.dragOperation = isCurrentlySelected ? "deselect" : "select";

			// Toggle the first cell immediately
			this.toggleHour(dayId, hour);
		},

		// Handle mouse enter on a cell during drag
		handleMouseEnter(dayId, hour) {
			if (!this.isDragging || !this.settings.availableDays[dayId] || !this.dragOperation) return;

			const currentHours = this.settings.availableHours[dayId] || {};
			const isCurrentlySelected = currentHours[hour];

			// Only toggle if the cell state doesn't match our drag operation
			if ((this.dragOperation === "select" && !isCurrentlySelected) ||
				(this.dragOperation === "deselect" && isCurrentlySelected)) {
				this.toggleHour(dayId, hour);
			}
		},

		// Handle mouse up to end dragging
		handleMouseUp() {
			this.isDragging = false;
			this.dragStartCell = null;
			this.dragOperation = null;
		},

		// Format a time (e.g., 14 -> "2 PM")
		formatTime(hour) {
			const date = new Date();
			date.setHours(hour, 0, 0, 0);
			return date.toLocaleTimeString("en-US", { hour: "numeric", hour12: true }).replace(":00", "");
		},

		// Get visible hours based on showAllHours setting
		getVisibleHours() {
			return this.showAllHours
				? Array.from({ length: 24 }, (_, i) => i) // 0-23 hours
				: this.HOURS; // 6 AM to 9 PM
		},

		// Check if an hour is available for a day
		isHourAvailable(dayId, hour) {
			return this.settings.availableHours[dayId]?.[hour] || false;
		},

		// Toggle show all hours
		setShowAllHours(value) {
			this.showAllHours = value;
		},

		// Handle save
		handleSave() {
			// 檢查每一天的所有時間，如果都是 false，則將該天設為不可用
			const daysToCheck = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
			
			daysToCheck.forEach(day => {
				// 檢查該天是否有任何時間是 true
				const hasAvailableHour = Object.values(this.settings.availableHours[day] || {}).some(value => value === true);
				
				// 如果沒有任何可用時間，將該天設為 false
				if (!hasAvailableHour) {
					this.settings.availableDays[day] = false;
				}
			});
			
			this.$emit('save', {
				...this.settings,
				lastUpdated: Date.now(),
			});
		}
	}
});
</script>

<style scoped>
.calendar-container {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 4px;
	overflow-x: auto;
/* Add horizontal scrolling for smaller screens */
}

.day-header {
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	display: flex;
	/* Ensure flex display */
	flex-direction: row;
	/* Force horizontal layout */
	flex-wrap: nowrap;
	/* Prevent wrapping */
	width: max-content;
/* Ensure it takes up necessary width */
}

.time-grid-container {
	max-height: 400px;
	overflow-y: auto;
	width: max-content;
/* Ensure it takes up necessary width */
}

.time-row {
	border-bottom: 1px solid rgba(0, 0, 0, 0.12);
	display: flex;
	/* Ensure flex display */
	flex-direction: row;
	/* Force horizontal layout */
	flex-wrap: nowrap;
/* Prevent wrapping */
}

.time-row:last-child {
	border-bottom: none;
}

.time-column {
	width: 80px;
	min-width: 80px;
	flex-shrink: 0;
	/* Prevent shrinking */
	border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.day-column {
	flex: 1;
	min-width: 40px;
	flex-shrink: 0;
	/* Prevent shrinking */
	border-right: 1px solid rgba(0, 0, 0, 0.12);
}

.day-column:last-child {
	border-right: none;
}

.selected-day {
	background-color: rgba(25, 118, 210, 0.05);
}

.unselected-day {
	background-color: rgb(241, 245, 249);
}

.selected-hour {
	background-color: rgba(25, 118, 210, 0.1);
}

.time-indicator {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
background-color: #e0e0e0;
}

.time-indicator.selected {
	background-color: #1976d2;
	color: white;
}

.cursor-pointer {
	cursor: pointer;
}

.cursor-not-allowed {
	cursor: not-allowed;
}
</style>